<template>
	<v-card class="mb-2">
		<v-card-text class="pa-3">
			<div class="d-flex align-baseline ga-3 flex-wrap temp-row">
				<v-tooltip location="top" open-on-click :text="targetHint">
					<template v-slot:activator="{ props: tipProps }">
						<div v-bind="tipProps" class="temp-block">
							<div class="temp-display-sm text-medium-emphasis">{{ fmt(s?.target) }}°F</div>
							<div class="text-caption text-medium-emphasis temp-label">target</div>
						</div>
					</template>
				</v-tooltip>
				<v-tooltip location="top" open-on-click :text="setpointHint">
					<template v-slot:activator="{ props: tipProps }">
						<div v-bind="tipProps" class="temp-block">
							<div class="temp-display text-primary">{{ fmt(s?.dock?.setpoint) }}°F</div>
							<div class="text-caption text-medium-emphasis temp-label">setpoint</div>
						</div>
					</template>
				</v-tooltip>
				<v-tooltip location="top" open-on-click :text="currentHint">
					<template v-slot:activator="{ props: tipProps }">
						<div v-bind="tipProps" class="temp-block">
							<div class="temp-display-sm">{{ fmt(s?.dock?.current) }}°F</div>
							<div class="text-caption text-medium-emphasis temp-label">current</div>
						</div>
					</template>
				</v-tooltip>
			</div>

			<div class="d-flex align-center flex-wrap ga-1 mt-2">
				<v-chip :color="dockColor" size="x-small">{{ dockLabel }}</v-chip>
				<v-chip v-if="s?.bed?.presence === true" color="success" size="x-small">occupied</v-chip>
				<v-chip v-else-if="s?.bed?.presence === false" size="x-small">not occupied</v-chip>
				<v-chip v-else size="x-small">no sensor</v-chip>
				<v-chip
					v-if="roomAdjust && roomDeltaLabel"
					color="primary"
					variant="tonal"
					prepend-icon="mdi-home-thermometer-outline"
					size="x-small"
				>{{ roomDeltaLabel }}</v-chip>
				<v-chip
					v-if="s?.autoVacatedAt != null"
					color="warning"
					variant="tonal"
					prepend-icon="mdi-bed-empty"
					size="x-small"
					closable
					close-label="Resume now"
					@click:close="resumeSnooze"
				>{{ s?.bed?.presence === true ? "auto-paused — waiting for schedule" : "auto-paused — bed empty" }}</v-chip>
				<v-chip
					v-if="s?.offWindowUntil != null"
					variant="tonal"
					prepend-icon="mdi-power-sleep"
					size="x-small"
				>off until {{ new Date(s.offWindowUntil).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) }}</v-chip>
			</div>

			<div v-if="nextEventLabel" class="text-caption text-medium-emphasis mt-1">
				next: {{ nextEventLabel }}
			</div>

			<div v-if="s?.water" class="text-caption mt-1 d-flex align-center flex-wrap ga-1">
				<v-icon icon="mdi-water-outline" size="small" />
				<span>water {{ waterLabel }}</span>
				<span v-if="s.water.lastRefilledAt" class="text-medium-emphasis">· refilled {{ new Date(s.water.lastRefilledAt).toLocaleDateString([], { month: "short", day: "numeric" }) }}</span>
				<v-chip v-if="s.water.alert === 'empty'" color="error" size="x-small">refill now</v-chip>
				<v-chip v-else-if="s.water.alert === 'low'" color="warning" size="x-small">top up soon</v-chip>
			</div>

			<div v-if="s?.bed?.presence" class="text-caption mt-1">
				HR {{ s.bed.heartRate ?? "—" }} · resp {{ s.bed.respiration ?? "—" }}
			</div>

			<v-divider class="my-3" />

			<div class="d-flex align-center flex-wrap ga-2">
				<v-btn
					v-if="!isManual && !isOff && !overrideActive"
					:loading="busyOverride"
					prepend-icon="mdi-tune"
					size="small"
					variant="tonal"
					@click="openOverride"
				>Override</v-btn>
				<v-btn
					v-if="!isManual && !isOff && overrideActive"
					:loading="busyOverride"
					prepend-icon="mdi-close"
					size="small"
					variant="tonal"
					color="warning"
					@click="cancelOverride"
				>Cancel Override</v-btn>
				<v-btn
					v-if="sleepInEligible"
					:loading="busySleep"
					prepend-icon="mdi-weather-night"
					size="small"
					variant="tonal"
					@click="sleepIn"
				>Sleep In</v-btn>
				<v-spacer />
				<v-btn
					v-if="dockRunning"
					:loading="busyDock"
					color="warning"
					size="small"
					variant="tonal"
					@click="stopDock"
				>Stop dock</v-btn>
				<v-btn
					v-else
					:loading="busyDock"
					color="primary"
					size="small"
					variant="tonal"
					@click="startDock"
				>Start dock</v-btn>
			</div>

			<div v-if="s?.sleepInUntil != null" class="text-caption text-medium-emphasis mt-2 d-flex align-center flex-wrap ga-1">
				<v-icon icon="mdi-weather-night" size="small" />
				<span>sleeping in until {{ new Date(s.sleepInUntil).toLocaleTimeString() }}</span>
				<v-btn variant="text" size="x-small" :loading="busySleep" @click="addSleepIn">+1h</v-btn>
				<v-btn variant="text" size="x-small" :loading="busySleep" @click="cancelSleepIn">Cancel</v-btn>
			</div>

			<v-expand-transition>
				<div v-if="sliderVisible" class="mt-3">
					<div class="d-flex align-center justify-center ga-3 mb-2">
						<v-btn
							icon="mdi-minus"
							size="large"
							variant="tonal"
							color="primary"
							:disabled="pendingSetpoint <= 55"
							@click="bumpSetpoint(-1)"
							aria-label="Decrease one degree"
						/>
						<div class="text-h4 setpoint-pending text-center">{{ pendingSetpoint }}°F</div>
						<v-btn
							icon="mdi-plus"
							size="large"
							variant="tonal"
							color="primary"
							:disabled="pendingSetpoint >= 110"
							@click="bumpSetpoint(1)"
							aria-label="Increase one degree"
						/>
					</div>
					<div v-if="s?.holdUntil" class="text-caption text-medium-emphasis d-flex align-center ga-1">
						<v-icon icon="mdi-timer-outline" size="x-small" />
						<span>{{ isManual ? "reverts to AUTO at " : "holds until " }}{{ holdUntilLabel }}</span>
					</div>
				</div>
			</v-expand-transition>
		</v-card-text>
	</v-card>

	<v-card>
		<v-card-text class="pa-3">
			<div class="d-flex align-center flex-wrap ga-2">
				<v-btn-toggle
					:model-value="s?.mode"
					@update:model-value="setMode"
					:disabled="busyMode"
					density="compact"
					mandatory
					divided
				>
					<v-btn value="auto" size="x-small">AUTO</v-btn>
					<v-btn value="manual" size="x-small">Manual</v-btn>
					<v-btn value="off" size="x-small">Off</v-btn>
				</v-btn-toggle>
				<v-spacer />
				<v-switch
					v-model="roomAdjust"
					color="primary"
					density="compact"
					hide-details
					:loading="busyRoom"
					:disabled="busyRoom"
					label="Room adj"
					class="secondary-switch"
				/>
			</div>
			<div class="text-caption text-medium-emphasis mt-1">{{ modeDescription }}</div>

			<div v-if="s?.pumpSnoozedTarget != null" class="text-caption text-medium-emphasis mt-2 d-flex align-center flex-wrap ga-1">
				<v-icon icon="mdi-pause-circle-outline" size="small" />
				<span>snoozed — auto resumes at next schedule change</span>
				<v-btn color="primary" variant="text" size="x-small" :loading="busyDock" @click="resumeSnooze">Resume now</v-btn>
			</div>
		</v-card-text>
	</v-card>
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";
import statusStore from "../stores/statusStore.js";
import schedulesStore from "../stores/schedulesStore.js";
import { nextScheduleEntry } from "../utils/scheduleResolver.js";
var props = defineProps({
	side: { type: String, required: true },
});

var status = statusStore;

var s = computed(() => statusStore.data.data?.sides?.[props.side]);

var pendingSetpoint = ref(68);
// Per-flow busy flags so e.g. clicking Stop dock doesn't make the Override button
// spin (they don't share state).
var busyOverride = ref(false);
var busyDock = ref(false);
var busySleep = ref(false);
var busyMode = ref(false);
var busyRoom = ref(false);
var overrideOpen = ref(false);

var isManual = computed(() => s.value?.mode === "manual");
var isOff = computed(() => s.value?.mode === "off");

// Override panel is "active" if the user opened it OR a hold is currently active
// (e.g. they committed an override or used warm/cool). The Override button is
// swapped for Cancel Override while active.
var overrideActive = computed(() => overrideOpen.value || s.value?.holdUntil != null);

// Slider is visible whenever:
//   - the side is in manual mode (always — that's how you adjust the target)
//   - the override panel is active (open or hold running)
// Always hidden in `off` mode — the app isn't supposed to touch the dock then.
var sliderVisible = computed(() => !isOff.value && (isManual.value || overrideActive.value));

function openOverride() {
	// Pre-fill slider with the current dock target so the first commit doesn't
	// jolt the bed unless the user actually moves the slider.
	var current = s.value?.dock?.setpoint;
	if (current != null) { pendingSetpoint.value = current; }
	overrideOpen.value = true;
}

async function cancelOverride() {
	busyOverride.value = true;
	try {
		// If there's an active hold (the override was committed), release it so the
		// loop resumes auto on the next tick. If no hold yet (panel open but never
		// committed), this is just a UI dismiss — no API call.
		if (s.value?.holdUntil != null) {
			await statusStore.methods.releaseHold(props.side);
		}
		overrideOpen.value = false;
	} finally { busyOverride.value = false; }
}

async function commitSetpoint() {
	busyOverride.value = true;
	try {
		if (isManual.value) {
			// Manual mode slider adjusts target only — pass holdMinutes=0 so the
			// server preserves any existing hold instead of starting a new one.
			await statusStore.methods.setSetpoint(props.side, pendingSetpoint.value, 0);
		} else {
			// Auto override: keep mode=auto on the server. holdMinutes omitted →
			// server defaults to "hold until next wake-schedule fire."
			await statusStore.methods.setSetpoint(props.side, pendingSetpoint.value, null, { preserveMode: true });
		}
	} finally { busyOverride.value = false; }
}

// +/- buttons: update the displayed value immediately and debounce the API
// call so multiple rapid taps batch into one write (sleepy 3am rapid-tapping
// shouldn't fire 5 PUTs and bounce the dock setpoint up and down).
var commitTimer = null;
function bumpSetpoint(delta) {
	var next = (pendingSetpoint.value ?? 68) + delta;
	if (next < 55) { next = 55; }
	if (next > 110) { next = 110; }
	pendingSetpoint.value = next;
	if (commitTimer) { clearTimeout(commitTimer); }
	commitTimer = setTimeout(() => { commitTimer = null; commitSetpoint(); }, 600);
}

// When a hold expires naturally (loop runs releaseHold and clears holdUntil),
// auto-collapse the override panel. Don't fire on the initial null→null state.
watch(() => s.value?.holdUntil, (now, prev) => {
	if (prev != null && now == null) { overrideOpen.value = false; }
});

watch(() => s.value?.dock?.setpoint, (v) => {
	// Don't overwrite the in-flight value while the user is mid-tap-storm,
	// otherwise a poll refresh between taps and the debounced commit would
	// snap the display back to the dock's old value.
	if (v != null && commitTimer == null) { pendingSetpoint.value = v; }
}, { immediate: true });

onMounted(() => schedulesStore.methods.load());

// If the hold expiry lines up with the wake-schedule fire, label it as such so
// users can see at a glance that the override is the new wake-bounded kind.
// Tolerance accounts for the few-second lag between override commit and the
// loop computing `holdUntil` (and DST-ish drift if a schedule edit happens).
var holdUntilLabel = computed(() => {
	var holdUntil = s.value?.holdUntil;
	if (!holdUntil) { return ""; }
	var timeStr = new Date(holdUntil).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
	var schedules = schedulesStore.data.bySide[props.side];
	if (!schedules?.length) { return timeStr; }
	// Find the next fire of the LAST entry in bedtime-cycle order (the wake entry).
	var enabled = schedules.filter((e) => e.enabled !== false);
	if (!enabled.length) { return timeStr; }
	var sorted = enabled.slice().sort((a, b) => {
		var ka = bedtimeKey(a.startTime);
		var kb = bedtimeKey(b.startTime);
		return ka - kb;
	});
	var wake = sorted[sorted.length - 1];
	var wakeMin = timeStrToMinutes(wake.startTime);
	if (wakeMin == null) { return timeStr; }
	var d = new Date(Date.now());
	var todayMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
	var todayFire = todayMidnight + wakeMin * 60_000;
	var wakeFire = todayFire > Date.now() ? todayFire : todayFire + 24 * 60 * 60_000;
	if (Math.abs(wakeFire - holdUntil) < 60_000) { return timeStr + " (wake)"; }
	return timeStr;
});

function timeStrToMinutes(s) {
	var m = /^(\d{1,2}):(\d{2})$/.exec(s ?? "");
	if (!m) { return null; }
	return Number(m[1]) * 60 + Number(m[2]);
}

function bedtimeKey(startTime) {
	var mins = timeStrToMinutes(startTime);
	if (mins == null) { return -1; }
	return mins >= 720 ? mins - 720 : mins + 720;
}

var nextEventLabel = computed(() => {
	statusStore.data.lastFetched; // re-evaluate each poll cycle
	var schedules = schedulesStore.data.bySide[props.side];
	if (!schedules?.length) { return null; }
	var result = nextScheduleEntry(Date.now(), schedules);
	if (!result) { return null; }
	var time = new Date(result.fireTs).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
	return result.entry.targetTemp + "°F at " + time;
});

var dockLabel = computed(() => {
	var dock = s.value?.dock;
	if (!dock) { return "no data"; }
	if (dock.connected === false) { return "offline"; }
	if (dock.thermalStatus === "active") { return "running"; }
	if (dock.thermalStatus === "standby") { return "standby"; }
	if (dock.thermalStatus === "off") { return "off"; }
	return dock.running ? "running" : "idle";
});

var dockColor = computed(() => {
	var dock = s.value?.dock;
	if (!dock || dock.connected === false) { return "error"; }
	if (dock.thermalStatus === "active") { return "success"; }
	if (dock.thermalStatus === "standby") { return undefined; }
	if (dock.thermalStatus === "off") { return "grey"; }
	return undefined;
});

// The docks only report 100, 50 or 0.
var waterLabel = computed(() => {
	var level = s.value?.water?.level;
	if (level >= 100) { return "full"; }
	if (level === 50) { return "half"; }
	if (level <= 0) { return "empty"; }
	return level + "%";
});

function fmt(v) {
	if (v == null) { return "—"; }
	return Number(v).toFixed(1);
}

var dockRunning = computed(() => s.value?.dock?.thermalStatus === "active");

async function stopDock() {
	busyDock.value = true;
	try { await statusStore.methods.stopDock(props.side); }
	finally { busyDock.value = false; }
}
async function startDock() {
	busyDock.value = true;
	try { await statusStore.methods.startDock(props.side); }
	finally { busyDock.value = false; }
}
async function resumeSnooze() {
	// startDock already clears pumpSnoozedTarget on the server.
	if (busyDock.value) { return; }
	await startDock();
}
async function setMode(m) {
	if (!m) { return; }
	busyMode.value = true;
	try { await statusStore.methods.setMode(props.side, m); }
	finally { busyMode.value = false; }
}

var roomAdjust = computed({
	get: () => {
		var raw = statusStore.data.data?.config?.roomAdjustEnabled;
		if (typeof raw === "boolean") { return raw; }
		return raw?.[props.side] !== false;
	},
	set: (v) => {
		busyRoom.value = true;
		statusStore.methods.setRoomAdjust(props.side, v).finally(() => { busyRoom.value = false; });
	},
});

var sleepInEligible = computed(() => s.value?.mode === "auto");

var modeDescription = computed(() => {
	switch (s.value?.mode) {
	case "auto": return "Automatically follows your schedule, adjusting for room temperature.";
	case "manual": return "You set the target with the slider; automation stays out of the way.";
	case "off": return "App is paused — make changes from the SleepMe app or dial.";
	default: return "";
	}
});

var roomDeltaLabel = computed(() => {
	if (!roomAdjust.value) { return null; }
	var d = statusStore.data.data;
	var roomTemp = d?.room?.temp;
	if (roomTemp == null) { return null; }
	var neutral = d?.config?.roomNeutralTemp ?? 72;
	var influence = d?.config?.roomTempInfluence ?? 0;
	if (!influence) { return null; }
	// adjusted setpoint = base - (room - neutral) * influence
	// so the room-driven offset relative to base is -(room - neutral) * influence
	var offset = -((roomTemp - neutral) * influence);
	if (Math.abs(offset) < 0.05) { return "room neutral"; }
	var sign = offset > 0 ? "+" : "−";
	return "room adj " + sign + Math.abs(offset).toFixed(1) + "°";
});


async function sleepIn() {
	busySleep.value = true;
	try { await statusStore.methods.sleepIn(props.side, 60); }
	finally { busySleep.value = false; }
}
async function addSleepIn() {
	busySleep.value = true;
	try { await statusStore.methods.sleepIn(props.side, 60); }
	finally { busySleep.value = false; }
}
async function cancelSleepIn() {
	busySleep.value = true;
	try { await statusStore.methods.cancelSleepIn(props.side); }
	finally { busySleep.value = false; }
}

// Hover hints — explain the three temperatures using the agreed terminology.
var targetHint = computed(() => {
	var base = "Target — the temperature your active schedule entry asks for (or the fallback in Settings if no schedule is set). Always shown regardless of mode, so you can see how far the setpoint has diverged.";
	if (s.value?.mode === "manual") {
		return base + " In manual mode the dock follows your slider, not this — they may differ.";
	}
	return base;
});
var setpointHint = computed(() => {
	if (s.value?.mode === "manual") {
		return "Setpoint — the temperature actually commanded to the dock. In manual mode this is your slider value; room compensation is off.";
	}
	if (roomAdjust.value && roomDeltaLabel.value && roomDeltaLabel.value !== "room neutral") {
		return "Setpoint — the temperature actually commanded to the dock. Equals target ± a room-temperature adjustment (currently " + roomDeltaLabel.value + ").";
	}
	return "Setpoint — the temperature actually commanded to the dock. With room adjust off or the room near neutral, setpoint equals target.";
});
var currentHint = computed(() => "Current — the water temperature the dock is reporting right now. It chases the setpoint over a few minutes.");
</script>

<style scoped>
.temp-row {
	row-gap: 4px;
}
.temp-block {
	cursor: help;
}
.temp-display {
	font-size: 2.25rem;
	font-weight: 600;
	line-height: 1.1;
}
.temp-display-sm {
	font-size: 1.5rem;
	font-weight: 500;
	line-height: 1.1;
}
.temp-label {
	text-transform: uppercase;
	letter-spacing: 0.04em;
}
.setpoint-pending {
	min-width: 70px;
}
.hold-expires-line {
	min-height: 1.4em;
}
.secondary-switch :deep(.v-label) {
	font-size: 0.8125rem;
	opacity: 0.85;
}
@media (max-width: 599px) {
	.temp-display {
		font-size: 1.875rem;
	}
	.temp-display-sm {
		font-size: 1.25rem;
	}
}
</style>
